import { supabase } from '../lib/supabase';
import errorTrackingService from './errorTrackingService';

export const propertyService = {
  /**
   * Get all properties for the current user
   */
  async getProperties(userId) {
    try {
      const { data, error } = await supabase?.from('properties')?.select(`
        *,
        offers(count),
        property_owners(name)
      `)?.eq('agent_id', userId)?.order('created_at', { ascending: false });

      if (error) {
        console.error('[Property Service] Error fetching properties:', error);
        
        // Provide user-friendly error messages for common issues
        if (error?.message?.includes('fetch') || error?.message?.includes('Failed to fetch')) {
          throw new Error('Network error: Unable to fetch properties. Please check your connection and try again.');
        } else if (error?.message?.includes('CORS')) {
          throw new Error('Connection error: Unable to reach the server. Please try again.');
        }
        
        throw error;
      }

      // Convert snake_case database fields to camelCase for frontend
      const mappedData = data?.map(property => ({
        ...property,
        isActive: property?.is_active,
        imageUrl: property?.image_url,
        imageAlt: property?.image_alt,
        listingPrice: property?.listing_price,
        propertyType: property?.property_type,
        commissionPercentage: property?.commission_percentage,
        agentId: property?.agent_id,
        createdAt: property?.created_at,
        lastReportDate: property?.last_report_date ? new Date(property?.last_report_date) : null,
        dateAdded: property?.created_at ? new Date(property?.created_at) : new Date(),
        offerCount: property?.offers?.[0]?.count || 0,
        owner: property?.property_owners?.[0]?.name || 'No Owner'
      }));

      return { data: mappedData, error: null };
    } catch (error) {
      console.error('Get properties error:', error);
      errorTrackingService?.captureApiError(
        '/properties',
        'GET',
        error,
        { userId },
        null
      );
      return { data: null, error: error?.message || 'Failed to fetch properties' };
    }
  },

  /**
   * Get a single property by ID
   */
  async getProperty(propertyId) {
    try {
      const { data, error } = await supabase?.from('properties')?.select(`
          *,
          property_owners(*)
        `)?.eq('id', propertyId)?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Get property error:', error);
      return { data: null, error: error?.message || 'Failed to fetch property' };
    }
  },

  /**
   * Create a new property
   */
  async createProperty(agentId, propertyData) {
    try {
      // Check subscription limits for property creation
      const { allowed, error: limitError } = await this.checkPropertyLimit(agentId);

      if (!allowed) {
        throw new Error(limitError || 'Exceeded property limit');
      }

      // Insert property
      const { data: property, error: propertyError } = await supabase?.from('properties')?.insert({
          agent_id: agentId,
          address: propertyData?.address,
          listing_price: propertyData?.listingPrice,
          property_type: propertyData?.propertyType || null,
          commission_percentage: propertyData?.commissionPercentage || null,
          image_url: propertyData?.imageUrl,
          image_alt: propertyData?.imageAlt,
          is_active: true
        })?.select()?.single();

      if (propertyError) throw propertyError;

      // Insert property owners if provided
      if (propertyData?.owners && propertyData?.owners?.length > 0) {
        const owners = propertyData?.owners?.filter(owner => owner?.name && owner?.email)?.map(owner => ({
            property_id: property?.id,
            name: owner?.name,
            email: owner?.email
          }));

        if (owners?.length > 0) {
          const { error: ownersError } = await supabase?.from('property_owners')?.insert(owners);

          if (ownersError) {
            console.error('Error inserting owners:', ownersError);
          }
        }
      }

      return { data: property, error: null };
    } catch (error) {
      console.error('Create property error:', error);
      return { data: null, error: error?.message || 'Failed to create property' };
    }
  },

  /**
   * Update an existing property
   */
  async updateProperty(propertyId, updates) {
    try {
      const updateData = {
        address: updates?.address,
        listing_price: updates?.listingPrice,
        property_type: updates?.propertyType || null,
        commission_percentage: updates?.commissionPercentage || null,
        image_url: updates?.imageUrl,
        image_alt: updates?.imageAlt,
        last_report_date: updates?.lastReportDate
      };

      // Only include is_active if explicitly provided in updates, otherwise keep existing value
      if (updates?.isActive !== undefined) {
        updateData.is_active = updates?.isActive;
      }

      const { data, error } = await supabase?.from('properties')?.update(updateData)?.eq('id', propertyId)?.select()?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Update property error:', error);
      return { data: null, error: error?.message || 'Failed to update property' };
    }
  },

  /**
   * Delete a property
   */
  async deleteProperty(propertyId) {
    try {
      const { error } = await supabase?.from('properties')?.delete()?.eq('id', propertyId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      console.error('Delete property error:', error);
      return { error: error?.message || 'Failed to delete property' };
    }
  },

  /**
   * Update property owners
   */
  async updatePropertyOwners(propertyId, owners) {
    try {
      // Delete existing owners
      await supabase?.from('property_owners')?.delete()?.eq('property_id', propertyId);

      // Insert new owners
      if (owners && owners?.length > 0) {
        const ownersData = owners?.filter(owner => owner?.name && owner?.email)?.map(owner => ({
            property_id: propertyId,
            name: owner?.name,
            email: owner?.email
          }));

        if (ownersData?.length > 0) {
          const { error } = await supabase?.from('property_owners')?.insert(ownersData);

          if (error) throw error;
        }
      }

      return { error: null };
    } catch (error) {
      console.error('Update property owners error:', error);
      return { error: error?.message || 'Failed to update property owners' };
    }
  },

  /**
   * Toggle property active/inactive state
   */
  async togglePropertyActive(propertyId, isActive) {
    try {
      const { data, error } = await supabase?.from('properties')?.update({
          is_active: isActive
        })?.eq('id', propertyId)?.select()?.single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Toggle property active error:', error);
      return { data: null, error: error?.message || 'Failed to update property status' };
    }
  },

  /**
   * Upload property photo to storage
   */
  async uploadPropertyPhoto(file, agentId) {
    try {
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${agentId}/${Date.now()}.${fileExt}`;

      const { data, error } = await supabase?.storage?.from('property-photos')?.upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase?.storage?.from('property-photos')?.getPublicUrl(fileName);

      return { data: { path: data?.path, url: publicUrl }, error: null };
    } catch (error) {
      console.error('Upload property photo error:', error);
      return { data: null, error: error?.message || 'Failed to upload photo' };
    }
  },

  /**
   * Delete property photo from storage
   */
  async deletePropertyPhoto(photoPath) {
    try {
      const { error } = await supabase?.storage?.from('property-photos')?.remove([photoPath]);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      console.error('Delete property photo error:', error);
      return { error: error?.message || 'Failed to delete photo' };
    }
  },

  // Check subscription limits for property creation
  async checkPropertyLimit(userId) {
    try {
      // Get user profile with subscription info
      const { data: profile, error: profileError } = await supabase
        ?.from('user_profiles')
        ?.select('subscription_tier, subscription_status')
        ?.eq('id', userId)
        ?.single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        return { allowed: false, error: profileError?.message };
      }

      const subscriptionTier = profile?.subscription_tier || 'free';
      const subscriptionStatus = profile?.subscription_status || 'inactive';

      // Pro users (active or trialing) have unlimited properties
      if (subscriptionTier === 'pro' && ['active', 'trialing']?.includes(subscriptionStatus)) {
        return { allowed: true, limit: null, current: null, tier: 'pro' };
      }

      // Free users: check if they have reached the 1 property limit
      const { count, error: countError } = await supabase
        ?.from('properties')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('agent_id', userId)
        ?.eq('is_active', true);

      if (countError) {
        console.error('Error counting properties:', countError);
        return { allowed: false, error: countError?.message };
      }

      const currentCount = count || 0;
      const limit = 1; // Free tier limit

      return {
        allowed: currentCount < limit,
        limit: limit,
        current: currentCount,
        tier: 'free',
        needsUpgrade: currentCount >= limit
      };
    } catch (error) {
      console.error('Check property limit error:', error);
      return { allowed: false, error: error?.message || 'Failed to check property limit' };
    }
  }
};